import {
  Activity,
  Boxes,
  CalendarClock,
  Gauge,
  Grid3x3,
  KeyRound,
  Mail,
  MailCheck,
  MapPin,
  MessageSquareText,
  PackageOpen,
  ScrollText,
  Settings2,
  ShieldCheck,
  SlidersHorizontal,
  UserCog,
  Users2,
  Workflow,
  type LucideIcon,
} from 'lucide-react';

/**
 * The parts of Administration, and who may open each.
 *
 * One list, so the hub's navigation, its overview page and its landing choice
 * cannot disagree — and so the shell can decide whether to show the
 * Administration link from the same facts (`ADMIN_ENTRY_PERMISSIONS`).
 *
 * `permission` is what the section's own list request needs, not a related
 * permission that merely sounds right. The service catalogue used to be gated
 * on `catalog.view`, which every supervisor, agent and requester holds, while
 * its list asks for `catalog.manage`: a supervisor opening Administration was
 * sent straight to a "you need catalog.manage" error that "Try again" could
 * never fix. Departments, locations and asset types are gated on managing them
 * for the same reason — reading them is everybody's, configuring them is not.
 */

export type AdminGroupKey =
  | 'service-desk'
  | 'channels'
  | 'catalog-assets'
  | 'organisation'
  | 'access'
  | 'system';

export const ADMIN_GROUPS: ReadonlyArray<{ key: AdminGroupKey; label: string }> = [
  { key: 'service-desk', label: 'Service desk' },
  { key: 'channels', label: 'Channels' },
  { key: 'catalog-assets', label: 'Catalog & assets' },
  { key: 'organisation', label: 'Organisation' },
  { key: 'access', label: 'Access & security' },
  { key: 'system', label: 'System' },
];

export interface AdminSection {
  /** The route key under `/admin`. Other screens mount at these, so they do not change. */
  path: string;
  label: string;
  description: string;
  icon: LucideIcon;
  group: AdminGroupKey;
  permission: string;
  /** Words people search for that the label does not say. */
  keywords: string[];
  /** For an entry that lives outside the hub, the page it opens. */
  to?: string;
}

export const ADMIN_SECTIONS: readonly AdminSection[] = [
  {
    path: 'settings',
    label: 'General',
    icon: Settings2,
    group: 'service-desk',
    permission: 'admin.settings.view',
    description: 'Desk name, default priority, closing resolved tickets, sign-in policy.',
    keywords: [
      'portal',
      'name',
      'default priority',
      'auto close',
      'session',
      'timeout',
      'idle',
      'sign out',
      'password',
      'expiry',
      'breach',
      'warning',
    ],
  },
  {
    path: 'sla',
    label: 'SLA policies',
    icon: Gauge,
    group: 'service-desk',
    permission: 'sla.view',
    description: 'How quickly each priority must be answered and resolved.',
    keywords: ['service level', 'targets', 'response', 'resolution', 'deadline', 'breach'],
  },
  {
    path: 'priority-matrix',
    label: 'Priority matrix',
    icon: Grid3x3,
    group: 'service-desk',
    // The same permissions as the default priority in General: the grid is
    // that decision made finer, and the API gates it the same way.
    permission: 'admin.settings.view',
    description: 'Which priority a ticket gets from its urgency and impact.',
    keywords: ['urgency', 'impact', 'itil', 'priority', 'grid'],
  },
  {
    path: 'business-hours',
    label: 'Business hours',
    icon: CalendarClock,
    group: 'service-desk',
    permission: 'sla.view',
    description: 'Working weeks and public holidays the SLA clock pauses for.',
    keywords: ['holidays', 'calendar', 'working week', 'eid', 'ramadan', 'timezone', 'shifts'],
  },
  {
    path: 'automation',
    label: 'Automation',
    icon: Workflow,
    group: 'service-desk',
    permission: 'automation.view',
    description: 'Rules that assign, update and notify on tickets by themselves.',
    keywords: ['rules', 'workflow', 'auto assign', 'trigger', 'routing', 'notify'],
  },
  {
    path: 'fields',
    label: 'Fields',
    icon: SlidersHorizontal,
    group: 'service-desk',
    permission: 'field.view',
    description: 'Custom fields on ticket, problem, change and asset forms.',
    keywords: ['custom fields', 'form', 'dropdown', 'required', 'portal'],
  },
  {
    path: 'reply-templates',
    label: 'Reply templates',
    icon: MessageSquareText,
    group: 'service-desk',
    permission: 'ticket.canned_response.manage',
    description: 'Stock wording agents start a reply from.',
    keywords: ['canned responses', 'macros', 'snippets'],
  },
  {
    path: 'mailboxes',
    label: 'Mailboxes',
    icon: Mail,
    group: 'channels',
    permission: 'email.settings.view',
    description: 'Where tickets arrive from and where replies are sent from.',
    keywords: ['email', 'inbound', 'smtp', 'imap', 'support address'],
  },
  {
    path: 'email-templates',
    label: 'Email templates',
    icon: MailCheck,
    group: 'channels',
    permission: 'email.settings.view',
    description: 'The messages the desk sends by itself.',
    keywords: ['notifications', 'email', 'acknowledgement'],
  },
  {
    path: 'email-log',
    label: 'Email log',
    icon: ScrollText,
    group: 'channels',
    permission: 'email.log.view',
    description: 'Every message in and out, and why any were dropped.',
    keywords: ['bounces', 'delivery', 'failed email'],
  },
  {
    path: 'catalog',
    label: 'Service catalog',
    icon: PackageOpen,
    group: 'catalog-assets',
    permission: 'catalog.manage',
    description: 'What people can ask for, and the form each request presents.',
    keywords: ['service items', 'requests', 'order', 'categories'],
  },
  {
    path: 'asset-types',
    label: 'Asset types',
    icon: Boxes,
    group: 'catalog-assets',
    permission: 'asset.manage_types',
    description: 'The kinds of thing the asset register tracks.',
    keywords: ['cmdb', 'ci types', 'hardware', 'panels', 'categories'],
  },
  {
    path: 'departments',
    label: 'Departments',
    icon: Users2,
    group: 'organisation',
    permission: 'org.department.manage',
    description: 'The business units requesters belong to.',
    keywords: ['teams', 'org chart', 'business units'],
  },
  {
    path: 'locations',
    label: 'Locations',
    icon: MapPin,
    group: 'organisation',
    permission: 'org.location.manage',
    description: 'Offices and sites people and assets are at.',
    keywords: ['sites', 'offices', 'address'],
  },
  {
    path: 'people',
    to: '/admin/people',
    label: 'People & groups',
    icon: UserCog,
    group: 'organisation',
    permission: 'user.view',
    description: 'Agents, requesters, groups and who belongs where.',
    keywords: ['users', 'agents', 'groups', 'members', 'roles'],
  },
  {
    path: 'roles',
    label: 'Roles',
    icon: ShieldCheck,
    group: 'access',
    permission: 'role.view',
    description: 'What each role may do, and how widely.',
    keywords: ['permissions', 'access', 'scope', 'rights'],
  },
  {
    path: 'sso',
    label: 'Single sign-on',
    icon: KeyRound,
    group: 'access',
    permission: 'admin.sso.manage',
    description: 'Sign in with a work account instead of a password.',
    keywords: ['saml', 'oidc', 'microsoft', 'google', 'azure'],
  },
  {
    path: 'audit',
    label: 'Audit log',
    icon: ScrollText,
    group: 'access',
    permission: 'admin.audit.view',
    description: 'Who changed what, and when.',
    keywords: ['history', 'changes', 'activity'],
  },
  {
    path: 'system',
    label: 'System',
    icon: Activity,
    group: 'system',
    permission: 'admin.job.view',
    description: 'Background work, stuck jobs and the database version.',
    keywords: ['jobs', 'queue', 'health', 'workers', 'migrations'],
  },
];

/**
 * Whether somebody holding these permissions has any part of the hub to open.
 *
 * The People entry is left out: it has its own place in the shell, and holding
 * `user.view` alone is not a reason to show an Administration link.
 */
export const ADMIN_ENTRY_PERMISSIONS: readonly string[] = [
  ...new Set(ADMIN_SECTIONS.filter((section) => !section.to).map((section) => section.permission)),
];

export function adminHref(section: AdminSection): string {
  return section.to ?? `/admin/${section.path}`;
}

export function visibleAdminSections(can: (permission: string) => boolean): AdminSection[] {
  return ADMIN_SECTIONS.filter((section) => can(section.permission));
}

/** The sections the hub itself can show, without the links that leave it. */
export function hubSections(sections: readonly AdminSection[]): AdminSection[] {
  return sections.filter((section) => !section.to);
}

/** Case-insensitive match on what a section is called, does and is known as. */
export function matchesAdminSearch(section: AdminSection, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === '') return true;
  return [section.label, section.description, ...section.keywords].some((text) =>
    text.toLowerCase().includes(needle),
  );
}
